import React from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { useAuth } from './hooks/useAuth';
import { useSegnalazioni } from './hooks/useSegnalazioni';
import { useUsers } from './hooks/useUsers';

import LandingPage from './components/LandingPage';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import AdminDashboard from './components/AdminDashboard';
import SegnalatoreDashboard from './components/SegnalatoreDashboard';
import Header from './components/Header';
import AdminNav from './components/AdminNav';
import SegnalatorNav from './components/SegnalatorNav';
import SegnalazioneForm from './components/SegnalazioneForm';
import AdminUsers from './components/AdminUsers';
import SegnalazioneDetail from './components/SegnalazioneDetail';

import './App.css';

function App() {
  const [currentView, setCurrentView] = React.useState('landing');
  const [selectedSegnalazione, setSelectedSegnalazione] = React.useState(null);

  // Auth hook
  const { user, userProfile, loading: authLoading, signUp, signIn, signOut } = useAuth();

  // Data hooks - only fetch when user is authenticated
  const { users, loading: usersLoading, deleteUser, refetch: refetchUsers } = useUsers();
  const { 
    segnalazioni, 
    loading: segnalazioniLoading, 
    createSegnalazione, 
    updateSegnalazione, 
    deleteSegnalazione, 
    refetch: refetchSegnalazioni 
  } = useSegnalazioni(
    userProfile?.id,
    userProfile?.role === 'admin'
  );

  // Set initial view based on user
  React.useEffect(() => {
    console.log('App useEffect - Auth state:', { 
      authLoading, 
      user: userProfile ? { id: userProfile.id, email: userProfile.email, role: userProfile.role } : null 
    });
    
    if (!authLoading && userProfile) {
      const targetView = userProfile.role === 'admin' ? 'admin-dashboard' : 'segnalatore-dashboard';
      console.log('Setting view to:', targetView);
      setCurrentView(targetView);
    } else if (!authLoading && !user) {
      console.log('No user, setting view to landing');
      setCurrentView('landing');
    }
  }, [user, userProfile, authLoading]);

  // Refresh data when user changes or signs in
  React.useEffect(() => {
    if (userProfile) {
      console.log('User authenticated, refreshing data...');
      refetchUsers();
      refetchSegnalazioni();
    }
  }, [userProfile]);

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <ThemeProvider>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="text-4xl mb-4">🏠</div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">HomeScout</h2>
            <p className="text-gray-600 dark:text-gray-400">Caricamento...</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  const appContext = {
    // Auth
    currentUser: userProfile,
    user,
    signUp,
    signIn,
    signOut,
    
    // Navigation
    currentView,
    setCurrentView,
    selectedSegnalazione,
    setSelectedSegnalazione,
    
    // Data
    users: users || [],
    segnalazioni: segnalazioni || [],
    
    // Actions
    createSegnalazione,
    updateSegnalazione,
    deleteSegnalazione,
    deleteUser,
    refetchSegnalazioni,
    refetchUsers,
    
    // Loading states
    usersLoading,
    segnalazioniLoading
  };

  console.log('App render - Current state:', {
    currentView,
    userRole: userProfile?.role,
    usersCount: users?.length || 0,
    segnalazioniCount: segnalazioni?.length || 0
  });

  return (
    <ThemeProvider>
      <div className="min-h-screen transition-colors">
        {!user ? (
          currentView === 'landing' ? (
            <LandingPage {...appContext} />
          ) : currentView === 'register' ? (
            <RegisterForm {...appContext} />
          ) : (
            <LoginForm {...appContext} />
          )
        ) : (
          <>
            <Header {...appContext} />
            
            {userProfile?.role === 'admin' ? (
              <AdminNav {...appContext} />
            ) : (
              <SegnalatorNav {...appContext} />
            )}

            {currentView === 'segnalatore-dashboard' && (
              <SegnalatoreDashboard {...appContext} />
            )}

            {currentView === 'admin-dashboard' && (
              <AdminDashboard {...appContext} />
            )}

            {currentView === 'admin-users' && (
              <AdminUsers {...appContext} />
            )}

            {currentView === 'nuova-segnalazione' && (
              <SegnalazioneForm {...appContext} />
            )}

            {currentView === 'modifica-segnalazione' && (
              <SegnalazioneForm 
                {...appContext} 
                editingSegnalazione={selectedSegnalazione} 
              />
            )}

            {selectedSegnalazione && currentView !== 'modifica-segnalazione' && (
              <SegnalazioneDetail
                {...appContext}
                segnalazione={selectedSegnalazione}
                onClose={() => setSelectedSegnalazione(null)}
              />
            )}
          </>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;